
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, User } from 'lucide-react';
import { format } from 'date-fns';
import { ChatLogDetailsDialog } from './ChatLogDetailsDialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface ChatLog {
  id: string;
  user_id: string;
  message: string;
  response: string;
  created_at: string;
  timestamp: string;
  user_name: string; // Changed from profiles object to direct user_name string
}

interface ChatLogsListProps {
  chatLogs: ChatLog[];
  isLoading: boolean;
  refetch: () => void;
}

export function ChatLogsList({ chatLogs, isLoading, refetch }: ChatLogsListProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedLogIds, setSelectedLogIds] = useState<string[]>([]);
  const [selectedLog, setSelectedLog] = useState<ChatLog | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // User profile navigation handler
  const handleUserClick = (userId: string, userName: string) => {
    // Navigate to user profile if user is registered (has ID)
    if (userId) {
      navigate(`/users/${userId}`);
    } else {
      toast({
        title: "Anonymous User",
        description: "This chat was from an anonymous user. No profile available.",
        variant: "default"
      });
    }
  };
  
  // View message handler
  const handleViewMessage = (chatLog: ChatLog) => {
    setSelectedLog(chatLog);
    setIsDialogOpen(true);
  };
  
  // Delete selected logs handler
  const handleDeleteSelected = async () => {
    try {
      // Only close dialog after successful deletion
      const { error } = await supabase
        .from('chat_logs')
        .delete()
        .in('id', selectedLogIds);
        
      if (error) throw error;
      
      toast({
        title: "Chat logs deleted",
        description: `Successfully deleted ${selectedLogIds.length} chat log(s)`,
      });
      
      // Reset selections and refetch
      setSelectedLogIds([]);
      refetch();
    } catch (error: any) {
      toast({
        title: "Error deleting chat logs",
        description: error.message || "An error occurred while deleting chat logs",
        variant: "destructive"
      });
    } finally {
      setIsDeleteDialogOpen(false);
    }
  };
  
  // Single log delete handler
  const handleDeleteLog = async (id: string) => {
    try {
      const { error } = await supabase
        .from('chat_logs')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast({
        title: "Chat log deleted",
        description: "The chat log was successfully deleted",
      });
      
      refetch();
    } catch (error: any) {
      toast({
        title: "Error deleting chat log",
        description: error.message || "An error occurred while deleting the chat log",
        variant: "destructive"
      });
    }
  };
  
  // Select all handler
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedLogIds(chatLogs.map(log => log.id));
    } else {
      setSelectedLogIds([]);
    }
  };
  
  // Select single log handler
  const handleSelectLog = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedLogIds(prev => [...prev, id]);
    } else {
      setSelectedLogIds(prev => prev.filter(logId => logId !== id));
    }
  };
  
  if (isLoading) {
    return <div>Loading chat logs...</div>;
  }
  
  return (
    <>
      <Card>
        <CardContent className="p-0">
          {selectedLogIds.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-muted/30 border-b">
              <span>{selectedLogIds.length} item(s) selected</span>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox 
                    checked={chatLogs.length > 0 && selectedLogIds.length === chatLogs.length}
                    onCheckedChange={(checked) => handleSelectAll(!!checked)}
                  />
                </TableHead>
                <TableHead>Date & Time</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Response</TableHead>
                <TableHead className="w-[80px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {chatLogs.length > 0 ? (
                chatLogs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Checkbox 
                        checked={selectedLogIds.includes(log.id)}
                        onCheckedChange={(checked) => handleSelectLog(log.id, !!checked)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">
                      {format(new Date(log.created_at || log.timestamp || ''), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        {log.user_id ? (
                          <Button 
                            variant="ghost" 
                            className="p-0 h-auto hover:bg-transparent"
                            onClick={() => handleUserClick(log.user_id, log.user_name)}
                          >
                            <span className="flex items-center">
                              <User className="h-4 w-4 mr-1" />
                              {log.user_name || 'Anonymous User'}
                            </span>
                          </Button>
                        ) : (
                          <span className="flex items-center text-muted-foreground">
                            <User className="h-4 w-4 mr-1" />
                            {log.user_name || 'Anonymous User'}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-left justify-start"
                        onClick={() => handleViewMessage(log)}
                      >
                        <span className="max-w-xs truncate block">{log.message}</span>
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-left justify-start"
                        onClick={() => handleViewMessage(log)}
                      >
                        <span className="max-w-xs truncate block">{log.response}</span>
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteLog(log.id)}
                        className="h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                        <span className="sr-only">Delete</span>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-4">
                    No chat logs found for the selected criteria
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Chat log details dialog */}
      <ChatLogDetailsDialog 
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        chatLog={selectedLog}
      />
      
      {/* Confirmation dialog for bulk deletion */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Chat Logs</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedLogIds.length} chat log(s)? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSelected}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
